import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { isMfaChallenge, ssoLogin, SsoLoginError, type LoginResponse } from '../api/auth'
import { CircularProgress } from '../components/ui'
import { clearLoggedOut, consumeState } from '../lib/equalPswSso'

interface Props {
  onLogin: (auth: LoginResponse) => void
}

/**
 * `/sso`: ritorno da Equal PSW (`?assertion=<JWT monouso>&state=<…>`), fuori dalle route protette.
 * Controlla lo state salvato da `startEqualPswLogin` prima di qualunque chiamata, poi manda
 * l'asserzione al backend. Riuscito: sessione come il login, poi la home. Con il secondo fattore
 * attivo: al login, sul passo del codice. Errore: `/login?error=<codice>`.
 */
export function SsoPage({ onLogin }: Props) {
  const navigate = useNavigate()
  // StrictMode esegue l'effetto due volte in sviluppo: l'asserzione vale una volta sola.
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true

    const params = new URLSearchParams(window.location.search)
    const assertion = params.get('assertion')
    const state = params.get('state')
    const fail = (code: string) => navigate(`/login?error=${encodeURIComponent(code)}`, { replace: true })

    // Prima lo state (lo consuma comunque): una risposta non partita da questa finestra si rifiuta
    // senza nemmeno guardare l'asserzione.
    if (!consumeState(state)) { fail('sso_state'); return }
    if (!assertion) { fail('sso_missing'); return }

    // L'asserzione esce subito dalla barra degli indirizzi e dalla cronologia.
    window.history.replaceState(window.history.state, '', window.location.pathname)

    ssoLogin(assertion)
      .then(result => {
        if (isMfaChallenge(result)) {
          navigate('/login', { replace: true, state: { mfaChallenge: result } })
          return
        }
        clearLoggedOut()
        onLogin(result)
        navigate('/', { replace: true })
      })
      .catch((err: unknown) => fail(err instanceof SsoLoginError ? err.code : 'sso_unavailable'))
  }, [navigate, onLogin])

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100svh' }}>
      <CircularProgress label="Accesso con Equal PSW in corso" />
    </div>
  )
}
